import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Clock, CheckCircle2, AlertTriangle, Loader2, X, Facebook,
  Instagram, Send, XCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  formatInTz,
  localInputToUtcIso,
  propertyTimezone,
  tzAbbreviation,
  utcIsoToLocalInput,
} from "@/lib/timezone";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

/**
 * Small helper: a datetime-local input pre-set to "60 min from now" so
 * clicking the checkbox once is a valid schedule out of the gate. Passes
 * back an ISO-8601 UTC string. The wall-clock value the editor sees is
 * interpreted as the property's newsroom timezone, not the browser's.
 */
export function ScheduleControls({
  enabled, setEnabled, isoValue, setIsoValue,
}) {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);

  useEffect(() => {
    if (enabled && !isoValue) {
      const soon = new Date(Date.now() + 60 * 60 * 1000); // +60 min
      soon.setSeconds(0, 0);
      setIsoValue(soon.toISOString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return (
    <div className="border-t border-[var(--border)] pt-3 mt-3">
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input
          data-testid="schedule-toggle"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="accent-[var(--slate-900)]"
        />
        <Clock className="w-3.5 h-3.5 text-[var(--ink-muted)]" />{tr("Schedule for later")}</label>
      {enabled && (
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              data-testid="schedule-datetime"
              type="datetime-local"
              value={utcIsoToLocalInput(isoValue, tz)}
              onChange={(e) =>
                setIsoValue(localInputToUtcIso(e.target.value, tz))
              }
              className="px-2 py-1.5 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
            />
            <span className="text-[11px] text-[var(--ink-muted)]">
              {isoValue
                ? formatInTz(isoValue, tz, "compact")
                : tr("pick a time")}
            </span>
          </div>
          <div className="text-[10px] text-[var(--ink-muted)] font-mono-display">{trRich("Times interpreted as <0>{tz}</0> ({tzAbbreviation}). Change in Settings → Newsroom timezone.", { tz, tzAbbreviation: tzAbbreviation(tz) }, [<span className="text-[var(--ink)]" />])}</div>
        </div>
      )}
    </div>
  );
}


/**
 * List of queued Facebook + Instagram posts for one article. Shows the
 * platform, target page/account, scheduled time, and a Cancel button
 * for any post still in the `pending` state. Refetches every 20 s so
 * posts flipping from pending → publishing → published are reflected.
 */
export default function ScheduledPostsList({ articleId }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.getSettings,
    staleTime: 5 * 60 * 1000,
  });
  const tz = propertyTimezone(settings);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["scheduled-posts", articleId],
    queryFn: () => api.listScheduledPosts({ article_id: articleId }),
    refetchInterval: 20000,
  });

  const cancel = useMutation({
    mutationFn: (id) => api.cancelScheduledPost(id),
    onSuccess: () => {
      toast.success(tr("Scheduled post cancelled"));
      qc.invalidateQueries({ queryKey: ["scheduled-posts", articleId] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || tr("Could not cancel")),
  });

  const items = data?.items || [];
  // Hide the whole component when there's nothing scheduled — no
  // reason to eat vertical space with an empty box.
  if (!isLoading && items.length === 0) return null;

  const relativeTime = (iso) => {
    const t = new Date(iso).getTime() - Date.now();
    const abs = Math.abs(t);
    const mins = Math.round(abs / 60000);
    if (mins < 60) return `${mins}m ${t < 0 ? tr("ago") : tr("from now")}`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ${t < 0 ? tr("ago") : tr("from now")}`;
    const days = Math.round(hours / 24);
    return `${days}d ${t < 0 ? tr("ago") : tr("from now")}`;
  };

  const statusMeta = {
    pending: {
      icon: Clock,
      tone: "text-amber-800 bg-amber-50 border-amber-200",
      label: tr("Scheduled"),
    },
    publishing: {
      icon: Loader2,
      tone: "text-sky-800 bg-sky-50 border-sky-200",
      label: tr("Publishing…"),
      spin: true,
    },
    published: {
      icon: CheckCircle2,
      tone: "text-emerald-800 bg-emerald-50 border-emerald-200",
      label: tr("Published"),
    },
    failed: {
      icon: AlertTriangle,
      tone: "text-red-800 bg-red-50 border-red-200",
      label: tr("Failed"),
    },
    cancelled: {
      icon: XCircle,
      tone: "text-neutral-700 bg-neutral-100 border-neutral-200",
      label: tr("Cancelled"),
    },
  };

  return (
    <section
      data-testid="scheduled-posts-list"
      className="mb-6 bg-white border border-[var(--border)]"
    >
      <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[var(--accent)]" />
          {trRich("<0>Scheduled posts · {itemCount}</0>", { itemCount: items.length }, [<span className="overline" />])}
        </div>
        {isFetching && (
          <Loader2 className="w-3 h-3 animate-spin text-[var(--ink-muted)]" />
        )}
      </div>
      <ul className="divide-y divide-[var(--border)]">
        {items.map((p) => {
          const meta = statusMeta[p.status] || statusMeta.pending;
          const Icon = meta.icon;
          const platIcon = p.platform === "instagram" ? Instagram : Facebook;
          const PlatIcon = platIcon;
          return (
            <li
              key={p.id}
              data-testid={`scheduled-post-${p.id}`}
              className="flex items-start gap-3 p-3"
            >
              <div className="shrink-0 w-8 h-8 grid place-items-center border border-[var(--border)] bg-[var(--secondary)]">
                <PlatIcon
                  className={
                    "w-4 h-4 " +
                    (p.platform === "instagram"
                      ? "text-fuchsia-700"
                      : "text-[#1877F2]")
                  }
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-sm font-medium truncate">
                    {p.platform === "instagram"
                      ? `@${p.ig_username || p.page_name}`
                      : p.page_name}
                  </span>
                  <span
                    className={
                      "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border " +
                      meta.tone
                    }
                  >
                    <Icon
                      className={
                        "w-3 h-3 " + (meta.spin ? "animate-spin" : "")
                      }
                    />
                    {meta.label}
                  </span>
                  {p.platform === "facebook" && (
                    <span className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">
                      {tr("{mode} post", { mode: p.mode })}</span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-muted)] mt-0.5">
                  {p.status === "published" && p.published_at
                    ? tr("Published {relativeTime} · {when}", { relativeTime: relativeTime(p.published_at), when: formatInTz(p.published_at, tz) })
                    : tr("Scheduled for {when} ({relativeTime})", { when: formatInTz(p.scheduled_for, tz), relativeTime: relativeTime(p.scheduled_for) })}
                </div>
                {p.error && (
                  <div className="text-xs text-red-700 mt-1 whitespace-pre-wrap">
                    {p.error}
                  </div>
                )}
                {p.caption && (
                  <div className="text-xs text-[var(--ink-muted)] mt-1 line-clamp-2 whitespace-pre-wrap">
                    {p.caption}
                  </div>
                )}
              </div>
              {p.status === "pending" && (
                <button
                  data-testid={`scheduled-cancel-${p.id}`}
                  onClick={() => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(tr("Cancel this scheduled post?"))) {
                      cancel.mutate(p.id);
                    }
                  }}
                  disabled={cancel.isPending}
                  className="shrink-0 inline-flex items-center gap-1 text-xs px-2 py-1 border border-transparent hover:border-red-200 hover:bg-red-50 text-[var(--ink-muted)] hover:text-red-700"
                >
                  <X className="w-3 h-3" />{tr("Cancel")}</button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
